/* eslint-disable prettier/prettier -- avoid formatting-only churn while iterating on this file */
import { getMetaOgTags } from "./metaOgTags";
import { DomainNameWithSubPathForLocale, GetDomainName } from "~/helpers/UrlHelper";
import { getRegionUrlPrefix, stripAndFormat } from "~/lib/helpers/slugHelper";
import {
  DEFAULT_LOGO_URL,
  DEFAULT_REGION,
} from "~/lib/constants/generalConstants";
import { getFormattedCountryName } from "~/lib/helpers/constants";
import { getCanonicalAndAlternates } from "./canonicalTags";

interface BlogPostLike {
  slug: string;
  country?: string;
  title?: string;
  webMetaDescription?: string;
  bannerImageUrl?: string;
  image?: string;
  authorName?: string;
  authorUrl?: string;
  publishDate?: string;
  lastModifiedDate?: string;
}

interface CountryMetaData {
  country?: string;
  region?: string;
  content?: Record<string, BlogPostLike[]>;
  regionData?: { region?: string };
}

interface ItemListNode {
  "@type": "ItemList";
  "@id": string;
  name: string;
  numberOfItems: number;
  itemListElement: {
    "@type": "ListItem";
    position: number;
    url: string;
    name?: string;
    item: { "@id": string };
  }[];
}

function slugIdFragment(label: string): string {
  const s = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return s || "posts";
}

function formatCategoryLabel(label: string): string {
  return label
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function decodeJsonLdText(text: string | undefined): string | undefined {
  if (text == null) return undefined;
  return text
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

function normalizeIsoDate(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  return iso.replace(/\+0000\b/g, "+00:00");
}

function dedupePostsBySlug(posts: BlogPostLike[]): BlogPostLike[] {
  const seen = new Set<string>();
  const out: BlogPostLike[] = [];
  for (const post of posts) {
    const slug = post?.slug;
    if (!slug || seen.has(slug)) continue;
    seen.add(slug);
    out.push(post);
  }
  return out;
}

export const newsParentMeta = ({
  data,
  path,
}: {
  data: CountryMetaData;
  path: string;
}) => {
  const regionConfig = data?.regionData;
  const { content } = data;

  const country = getFormattedCountryName(
    regionConfig?.region ?? DEFAULT_REGION,
  );
  const title = `Office Hub Blog | Workspace News, Guides & Market Insights`;
  const description = `Explore workspace news, office market insights, area guides, tenant advice, case studies, podcasts and flexible workspace trends across ${country}`;
  const localeBase = `${DomainNameWithSubPathForLocale(regionConfig?.region)}`;
  const blogPageUrl = `${localeBase}/blog`;
  const canonicalUrl = `${GetDomainName()}`;
  let Url =
    canonicalUrl.endsWith("/") && path === "/"
      ? canonicalUrl.slice(0, -1)
      : canonicalUrl + (path === "/" ? "" : path);

  const organizationId = `${localeBase}/#organization`;
  const websiteId = `${localeBase}/#website`;
  const collectionPageId = `${blogPageUrl}/#webpage`;

  const sameAs = [
    "https://www.facebook.com/officehubworkspaces",
    "https://twitter.com/officehubtweet",
    "https://www.pinterest.com/officehubaus",
    "https://www.linkedin.com/company/office-hub-pty-ltd",
    "https://www.youtube.com/channel/UCt4FPeBFmHtejAWDVk0J-4A",
  ];

  const getArticleUrl = (slug: string) => `${localeBase}/blog/${slug}`;
  const getItemListArticleUrl = (post: BlogPostLike) => {
    const prefix = getRegionUrlPrefix(post.country);
    return `${canonicalUrl}${prefix}/blog/${post.slug}`;
  };
  const getArticleId = (post: BlogPostLike) =>
    `${getItemListArticleUrl(post)}#article`;

  const buildBlogPosting = (post: BlogPostLike) => {
    const articleUrl = getArticleUrl(post.slug);
    const articleId = getArticleId(post);
    const desc = decodeJsonLdText(post.webMetaDescription);
    const author: Record<string, unknown> = {
      "@type": "Person",
      name: post.authorName || "Office Hub",
      url: `${localeBase}/meet-the-team/${stripAndFormat(post.authorName)}`,
    };
    if (post.authorUrl?.trim()) {
      author.url = post.authorUrl.trim();
    }
    return {
      "@type": "BlogPosting",
      "@id": articleId,
      headline: post.title,
      ...(desc != null ? { description: desc } : {}),
      image: post.bannerImageUrl || post.image,
      author,
      datePublished: normalizeIsoDate(post.publishDate),
      dateModified: normalizeIsoDate(post.lastModifiedDate || post.publishDate),
      url: articleUrl,
      publisher: { "@id": organizationId },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": articleUrl,
      },
    };
  };

  const categoryEntries = content
    ? [...Object.entries(content)].sort(([a], [b]) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    )
    : [];

  const articleNodeMap = new Map<string, ReturnType<typeof buildBlogPosting>>();

  const itemListNodes: ItemListNode[] = categoryEntries.map(([categoryLabel, rawPosts]) => {
    const posts = dedupePostsBySlug(Array.isArray(rawPosts) ? rawPosts : []);
    const listId = `${blogPageUrl}/#itemlist-${slugIdFragment(categoryLabel)}`;
    return {
      "@type": "ItemList",
      "@id": listId,
      name: `${formatCategoryLabel(categoryLabel)} | Office Hub Blog`,
      numberOfItems: posts.length,
      itemListElement: posts.map((post, index) => {
        const articleId = getArticleId(post);
        const articleUrl = getItemListArticleUrl(post);
        if (!articleNodeMap.has(articleId)) {
          articleNodeMap.set(articleId, buildBlogPosting(post));
        }
        return {
          "@type": "ListItem",
          position: index + 1,
          url: articleUrl,
          ...(post.title ? { name: post.title } : {}),
          item: { "@id": articleId },
        };
      }),
    };
  });
  const articleNodes = Array.from(articleNodeMap.values());

  const mainListId =
    categoryEntries.length > 0
      ? `${blogPageUrl}/#itemlist-${slugIdFragment(categoryEntries[0][0])}`
      : `${blogPageUrl}/#blog-list`;

  const graph: any[] = [
    {
      "@type": "Organization",
      "@id": organizationId,
      name: "Office Hub",
      url: localeBase,
      logo: {
        "@type": "ImageObject",
        url: DEFAULT_LOGO_URL,
      },
      sameAs,
    },
    {
      "@type": "WebSite",
      "@id": websiteId,
      url: localeBase,
      name: "Office Hub",
      publisher: { "@id": organizationId },
    },
    {
      "@type": "CollectionPage",
      "@id": collectionPageId,
      url: blogPageUrl,
      name: title,
      description,
      isPartOf: { "@id": websiteId },
      publisher: { "@id": organizationId },
      ...(itemListNodes.length ? { mainEntity: { "@id": mainListId } } : {}),
      ...(itemListNodes.length
        ? { hasPart: itemListNodes.map((node) => ({ "@id": node["@id"] })) }
        : {}),
    },
    ...itemListNodes,
    ...articleNodes,
  ];

  const ldJson = [
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@graph": graph,
      },
    },
  ];

  return [
    ...getMetaOgTags({
      title,
      description,
      imageUrl: DEFAULT_LOGO_URL,
      canonicalUrl: Url,
    }),
    ...getCanonicalAndAlternates(canonicalUrl, path),
    ...ldJson,
  ];
};
